import { jsx } from 'slate-hyperscript';
import type { Descendant, Editor, Element, Text } from 'slate';
import { Transforms } from 'slate';
import type { HTMLElements, HTMLLeafProps } from './types';

export const ELEMENT_TAGS: Record<
  string,
  (el: HTMLElement) => Omit<HTMLElements, 'children'>
> = {
  A: (el) => ({ type: 'link', url: el.getAttribute('href')! }),
  BLOCKQUOTE: () => ({ type: 'block-quote' }),
  H1: () => ({ type: 'heading-one' }),
  H2: () => ({ type: 'heading-two' }),
  H3: () => ({ type: 'heading-three' }),
  H4: () => ({ type: 'heading-four' }),
  H5: () => ({ type: 'heading-five' }),
  H6: () => ({ type: 'heading-six' }),
  IMG: (el) => ({ type: 'image', url: el.getAttribute('src')! }),
  LI: () => ({ type: 'list-item' }),
  OL: () => ({ type: 'numbered-list' }),
  P: () => ({ type: 'paragraph' }),
  PRE: () => ({ type: 'code-block' }),
  UL: () => ({ type: 'bulleted-list' }),
};

export const TEXT_TAGS: Record<string, () => HTMLLeafProps> = {
  CODE: () => ({ code: true }),
  DEL: () => ({ strikethrough: true }),
  EM: () => ({ italic: true }),
  I: () => ({ italic: true }),
  S: () => ({ strikethrough: true }),
  STRONG: () => ({ bold: true }),
  U: () => ({ underline: true }),
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
function _deserialize(el: HTMLElement | ChildNode): any {
  if (el.nodeType === 3) {
    return { text: el.textContent };
  } else if (el.nodeType !== 1) {
    return null;
  } else if (el.nodeName === 'BR') {
    return { text: '\n' };
  }

  const { nodeName } = el;
  let { childNodes } = el;

  if (nodeName === 'PRE' && childNodes[0] && childNodes[0].nodeName === 'CODE') {
    childNodes = el.childNodes[0].childNodes;
  }

  let children = Array.from(childNodes).map(deserialize);

  if (children.length === 0) {
    children = [{ text: '' }];
  }

  if (nodeName === 'BODY') {
    return jsx('fragment', {}, children);
  }

  if (ELEMENT_TAGS[nodeName]) {
    const attrs = ELEMENT_TAGS[nodeName](el as HTMLElement);
    return jsx('element', attrs, children);
  }

  if (TEXT_TAGS[nodeName]) {
    const attrs = TEXT_TAGS[nodeName]();
    return children.map((child) => jsx('text', attrs, child));
  }

  return children;
}

export function deserialize(el: HTMLElement | ChildNode): any {
  const roots: Descendant[] = [];
  const rootNodes = el.childNodes;

  for (const root of rootNodes) {
    const children: Descendant[] = [];

    const stack = [root];

    while (stack.length) {
      const node = stack.shift()!;
      const { nodeName, childNodes, nodeType, textContent } = node;

      if (nodeType === Node.TEXT_NODE) {
        const textContent_ = textContent?.trim();
        if (textContent_) {
          children.push(jsx('text', textContent_));
        }
      } else if (nodeName === 'BR') {
        children.push(jsx('text', '\n'));
      } else if (nodeType === Node.ELEMENT_NODE) {
        if (childNodes.length !== 0) {
          stack.unshift(...childNodes);
        }
      }
    }

    if (children.length) {
      roots.push(jsx('element', children));
    }
  }

  return jsx('fragment', {}, roots);
}

export function isHTMLEls(element: Element) {
  const types: Array<HTMLElements['type']> = [
    'paragraph',
    'block-quote',
    'heading-one',
    'heading-two',
    'heading-three',
    'heading-four',
    'heading-five',
    'heading-six',
    'image',
    'list-item',
    'numbered-list',
    'code-block',
    'bulleted-list',
  ];

  return types.includes(element.type as any);
}

export function isHTMLLeaf(leaf: Text) {
  const props: Array<keyof HTMLLeafProps> = [
    'code',
    'strikethrough',
    'italic',
    'bold',
    'underline',
  ];
  return props.some((p) => p in leaf);
}

export function withHTMLEditor(editor: Editor) {
  const { insertData, isVoid } = editor;

  editor.isVoid = (element: Element) => {
    return element.type === 'image' || isVoid(element);
  };

  editor.insertData = (data) => {
    const html = data.getData('text/html');

    if (html) {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const fragment = deserialize(parsed.body);
      Transforms.insertFragment(editor, fragment);
      return;
    }

    insertData(data);
  };

  return editor;
}
